// ============================================================================
// PRICING PAGE
// PRO membership plans with Razorpay integration
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Check, Sparkles, Zap, Crown } from 'lucide-react';

const PricingPage = ({ isMobile, onOpenAuth, onNavigate }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('auth_token');
    const userInfo = localStorage.getItem('user_info');

    if (token && userInfo) {
      setUser(JSON.parse(userInfo));
      fetchSubscription(token);
    }

    // Handle OAuth redirect callback
    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get('auth_token');
    const userParam = urlParams.get('user');
    const message = urlParams.get('message');

    if (message) {
      setNotice(decodeURIComponent(message));
    }

    if (authToken && userParam) {
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('user_info', userParam);
      setUser(JSON.parse(userParam));

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      fetchSubscription(authToken);
    }
  }, []);

  const fetchSubscription = async (token) => {
    try {
      const response = await fetch('https://theresumex.cv/api/user/subscription', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.subscription) {
        setSubscription(data.subscription);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
  };

  const handlePurchase = async (plan) => {
    if (!user) {
      onOpenAuth();
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('auth_token');

      // Create Razorpay order
      const orderResponse = await fetch('https://theresumex.cv/api/payment/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plan: plan.id })
      });

      const orderData = await orderResponse.json();

      if (!orderData.success) {
        throw new Error('Failed to create order');
      }

      // Load Razorpay script
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        const options = {
          key: orderData.keyId,
          amount: orderData.amount, // Amount already in paise from backend
          currency: orderData.currency,
          name: 'ResumeX PRO',
          description: `${plan.analyzes} AI Analyzes`,
          order_id: orderData.orderId,
          handler: async function (response) {
            console.log('Payment successful! Response:', response);

            // Verify payment
            try {
              console.log('Calling verify-payment API...');
              const verifyResponse = await fetch('https://theresumex.cv/api/payment/verify-payment', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  plan: plan.id,
                  analyzes: plan.analyzes
                })
              });

              console.log('Verify response status:', verifyResponse.status);
              const verifyData = await verifyResponse.json();
              console.log('Verify response data:', verifyData);

              if (verifyData.success) {
                alert(`🎉 Payment Successful! You now have ${verifyData.subscription.remainingAnalyzes} AI analyzes available.`);
                setSubscription(verifyData.subscription);

                // Redirect to AI Analyzer after successful payment
                setTimeout(() => {
                  if (onNavigate) {
                    onNavigate('ai-analyzer');
                  } else {
                    window.location.href = '/#ai-analyzer';
                  }
                }, 1500);
              } else {
                console.error('Payment verification failed:', verifyData);
                alert('Payment verification failed. Please contact support at info.theresumex@gmail.com');
              }
            } catch (err) {
              console.error('Payment verification error:', err);
              alert('Payment verification error. Please contact support at info.theresumex@gmail.com with your payment ID: ' + response.razorpay_payment_id);
            } finally {
              setLoading(false);
            }
          },
          prefill: {
            email: user.email,
            name: user.name
          },
          theme: {
            color: '#145A64'
          },
          modal: {
            ondismiss: function() {
              setLoading(false);
              console.log('Payment modal dismissed');
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      };
    } catch (err) {
      alert('Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  const plans = [
    {
      id: '5_analyzes',
      name: 'Starter',
      analyzes: 5,
      price: '$1.49',
      inr: '₹124',
      icon: Sparkles,
      color: '#3B82F6',
      features: [
        '5 AI-powered analyzes',
        'Resume improvement suggestions',
        'Keyword optimization',
        'ATS compatibility check'
      ]
    },
    {
      id: '10_analyzes',
      name: 'Popular',
      analyzes: 10,
      price: '$2.00',
      inr: '₹166',
      icon: Zap,
      color: '#10B981',
      popular: true,
      features: [
        '10 AI-powered analyzes',
        'All Starter features',
        'Section rewriting',
        'Before/after comparison'
      ]
    },
    {
      id: '15_analyzes',
      name: 'Pro',
      analyzes: 15,
      price: '$3.49',
      inr: '₹290',
      icon: Crown,
      color: '#8B5CF6',
      features: [
        '15 AI-powered analyzes',
        'All Popular features',
        'Priority support',
        'Best value per analyze'
      ]
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      padding: isMobile ? '40px 20px' : '60px 40px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h1 style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '800',
            color: '#145A64',
            marginBottom: '16px'
          }}>
            Upgrade to PRO
          </h1>
          <p style={{
            fontSize: isMobile ? '16px' : '20px',
            color: '#666',
            maxWidth: '600px',
            margin: '0 auto 24px'
          }}>
            Get AI-powered resume enhancements with GPT-4o. Choose a plan that fits your needs.
          </p>

          {notice && (
            <div style={{
              display: 'inline-block',
              padding: '14px 24px',
              background: '#FEF3C7',
              color: '#92400E',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              marginBottom: '20px',
              border: '2px solid #FCD34D'
            }}>
              ℹ️ {notice}
            </div>
          )}

          {subscription && (
            <div style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: '#10B981',
              color: 'white',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600'
            }}>
              ✨ You have {subscription.remainingAnalyzes} AI analyzes remaining
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '24px',
          marginBottom: '60px'
        }}>
          {plans.map((plan) => {
            const Icon = plan.icon;
            return (
              <div
                key={plan.id}
                style={{
                  background: 'white',
                  borderRadius: '20px',
                  padding: '32px',
                  border: plan.popular ? `3px solid ${plan.color}` : '2px solid #e5e7eb',
                  position: 'relative',
                  boxShadow: plan.popular ? '0 10px 40px rgba(0,0,0,0.1)' : '0 4px 12px rgba(0,0,0,0.05)',
                  transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.3s'
                }}
              >
                {plan.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: plan.color,
                    color: 'white',
                    padding: '6px 20px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '700'
                  }}>
                    MOST POPULAR
                  </div>
                )}

                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '14px',
                  background: `${plan.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px'
                }}>
                  <Icon size={28} color={plan.color} />
                </div>

                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#145A64',
                  marginBottom: '8px'
                }}>
                  {plan.name}
                </h3>

                <div style={{ marginBottom: '20px' }}>
                  <span style={{
                    fontSize: '40px',
                    fontWeight: '800',
                    color: '#1f2937'
                  }}>
                    {plan.price}
                  </span>
                  <span style={{
                    fontSize: '16px',
                    color: '#9ca3af',
                    marginLeft: '8px'
                  }}>
                    ({plan.inr})
                  </span>
                </div>

                <div style={{
                  background: `${plan.color}10`,
                  padding: '12px',
                  borderRadius: '10px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <span style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: plan.color
                  }}>
                    {plan.analyzes} AI Analyzes
                  </span>
                </div>

                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 28px 0'
                }}>
                  {plan.features.map((feature, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '12px',
                      fontSize: '14px',
                      color: '#4b5563'
                    }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: `${plan.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Check size={12} color={plan.color} strokeWidth={3} />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePurchase(plan)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: loading ? '#ccc' : (plan.popular ? plan.color : 'white'),
                    color: plan.popular ? 'white' : plan.color,
                    border: `2px solid ${plan.color}`,
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.background = plan.color;
                      e.target.style.color = 'white';
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.target.style.background = plan.popular ? plan.color : 'white';
                      e.target.style.color = plan.popular ? 'white' : plan.color;
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {loading ? 'Processing...' : (user ? 'Purchase Now' : 'Sign in to Purchase')}
                </button>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: isMobile ? '32px 24px' : '40px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#145A64',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Frequently Asked Questions
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                What are AI analyzes?
              </h3>
              <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                Each AI analyze uses GPT-4o to review your resume and provide personalized suggestions, keyword optimization, section rewrites, and comprehensive improvements.
              </p>
            </div>

            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                Do credits expire?
              </h3>
              <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                No! Your AI analyzes never expire. Use them whenever you need to improve your resume.
              </p>
            </div>

            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                Can I purchase more analyzes later?
              </h3>
              <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                Absolutely! You can purchase any plan at any time. Your new analyzes will be added to your remaining balance.
              </p>
            </div>

            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                Is my payment information secure?
              </h3>
              <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                Yes! All payments are processed securely through Razorpay. We never store your payment information.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
